import { Navigate, useLocation, useParams } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { Button, SectionCard } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { Screen } from "@/shared/ui";
import { isDriverOnly, landingPathFor } from "./landing";
import { useLogout } from "./useLogout";
import { useStoreBinding } from "./useStoreBinding";

/**
 * "USE THE TRIOTY DRIVER APP" — the one screen a driver-only account sees on
 * the web (driver backend B1, plan D-5).
 *
 * A DRIVER holds `RESTAURANT_DRIVER_WORK` and nothing the till, the board or
 * the back office needs (`isDriverOnly`), so every one of those routes would
 * open on calls that 403. The driver's work — offers, bags, cash — is in the
 * Trioty driver app; this page says so, names the restaurant, and offers the
 * only two things a driver can usefully do here: sign out, or switch to
 * another restaurant on the same account where they may hold a real role.
 *
 * Rendered by `ProtectedLayout` for EVERY protected route once the session is
 * driver-only (through `DriverOnlyGate`, which first moves the URL to
 * `/:locale/driver`), before any provider, alert or poller of the till mounts.
 * The `driver` route itself renders this page too; a session that is NOT
 * driver-only is sent on to where it belongs, so the URL never strands a
 * cashier on a screen meant for someone else.
 */
export function DriverAppLandingPage() {
  const t = useTranslate();
  const { locale = "ar" } = useParams();
  const { logout, isLoggingOut } = useLogout();
  const {
    session,
    restaurantStores,
    switchTo,
    isSwitching,
    switchingTo,
    switchError,
  } = useStoreBinding();

  if (session && !isDriverOnly(session.seller.permissions)) {
    return <Navigate to={landingPathFor(locale, session)} replace />;
  }

  const storeName = session?.seller.storeName ?? t((m) => m.common.appName);

  return (
    <Screen title={storeName}>
      <div
        className="mx-auto flex w-full max-w-[480px] flex-col gap-3"
        data-testid="driver-landing"
      >
        <SectionCard title={t((m) => m.driverAccess.landingTitle)}>
          <div className="flex flex-col gap-3 text-sm">
            <span
              aria-hidden="true"
              className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-control,8px)] bg-[color:var(--surface-alt)] text-[color:var(--text-primary)]"
            >
              <Smartphone size={22} strokeWidth={1.8} />
            </span>
            <p>{t((m) => m.driverAccess.landingBody)}</p>
            {session ? (
              <p className="font-semibold" data-testid="driver-landing-store">
                {t((m) => m.driverAccess.landingStore, {
                  store: session.seller.storeName,
                })}
              </p>
            ) : null}
            <p className="text-[color:var(--text-muted)]">
              {t((m) => m.driverAccess.landingHint)}
            </p>
          </div>
        </SectionCard>

        {restaurantStores.length > 0 ? (
          <SectionCard title={t((m) => m.driverAccess.landingSwitchTitle)}>
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                {t((m) => m.driverAccess.landingSwitchBody)}
              </p>
              {restaurantStores.map((store) => (
                <Button
                  key={store.storeId}
                  variant="outline"
                  size="touch"
                  fullWidth
                  isLoading={isSwitching && switchingTo === store.storeSlug}
                  disabled={isSwitching}
                  onClick={() => void switchTo(store.storeSlug)}
                  data-testid={`driver-landing-switch-${store.storeSlug}`}
                >
                  {store.storeName}
                </Button>
              ))}
              {switchError ? (
                <p role="alert" className="text-sm text-[color:var(--danger)]">
                  {switchError}
                </p>
              ) : null}
            </div>
          </SectionCard>
        ) : null}

        <Button
          variant="primary"
          size="touch"
          fullWidth
          isLoading={isLoggingOut}
          disabled={isLoggingOut}
          onClick={logout}
          data-testid="driver-landing-logout"
        >
          {t((m) => m.common.logout)}
        </Button>
      </div>
    </Screen>
  );
}

/**
 * What `ProtectedLayout` renders for a driver-only session on ANY protected
 * route: a deep link to `/pos`, `/kitchen` or `/manage/...` lands on the
 * driver screen, under its own URL.
 */
export function DriverOnlyGate() {
  const { locale = "ar" } = useParams();
  const { pathname } = useLocation();
  const target = `/${locale}/driver`;
  if (pathname !== target) return <Navigate to={target} replace />;
  return <DriverAppLandingPage />;
}
